// Copyright (c) 2024. UpCircle Technology AG.
// All rights reserved. Redistribution in source and/or binary forms,
// with or without modification, is not permitted.

import React, { useMemo } from 'react';
import './PropertyField.scss';
import { useTranslation } from 'react-i18next';

import { doc } from 'firebase/firestore';
import { firestore } from '../../context/firebase';

import PropertyField from './PropertyField';

import location_icon from '../../assets/icons/white/location.png';
import { STOCK_FIELDS_DOCUMENT_PATH } from '../../shared_components/utils/FirebaseUtils';
import { useAuthStore } from '../../stores/authStore';
import CommonModal from '../../shared_components/CommonModal/CommonModal';
import { useSetDocument } from '../../shared_components/hooks/useSetDocument';

export default function StockFieldPropertyField({ value, handleDeliveryFieldChange }) {
  const { t } = useTranslation();
  const currentUserClientId = useAuthStore((state) => state.currentUser?.company?.company);
  const stockFields = useAuthStore((state) => state.stockFields);
  const [showAddOptionModal, setShowAddOptionModal] = React.useState(false);
  const stockFieldInputRef = React.useRef(null);

  const stockFieldDocumentRef = doc(firestore, STOCK_FIELDS_DOCUMENT_PATH(currentUserClientId));
  const { setDocument: setStockFieldDocument } = useSetDocument(stockFieldDocumentRef);

  // Derive options from stock field data
  const options = useMemo(() => {
    const newOptions = {};
    Object.entries(stockFields).forEach(([key, stock_field]) => {
      newOptions[key] = stock_field.display_name;
    });
    // Check if the current value is not in the options list. If not, add it.
    if (value && !Object.keys(newOptions).includes(value)) {
      newOptions[value] = value;
    }
    return newOptions;
  }, [stockFields, value]);

  function addOption() {
    /* Add a new stock field to the stock field document */

    // Check that the input fields are not empty
    if (!stockFieldInputRef.current.value || stockFieldInputRef.current.value.length === 0) {
      setShowAddOptionModal(false);
      return;
    }

    const id = stockFieldInputRef.current.value.toLowerCase().replace(/ /g, '_');

    // Check if ID already exists
    if (Object.keys(options).includes(id)) {
      setShowAddOptionModal(false);
      return;
    }

    setStockFieldDocument(
      {
        fields: {
          [id]: {
            full_name: stockFieldInputRef.current.value,
            display_name: stockFieldInputRef.current.value,
          },
        },
      },
      { merge: true },
    );

    // Change delivery stock field to the new stock field
    handleDeliveryFieldChange({ target: { name: 'storage_field', value: id } });

    setShowAddOptionModal(false);
  }

  return (
    <>
      <PropertyField
        icon={location_icon}
        title={t('property_storage_field')}
        value={value}
        name={'storage_field'}
        type='select-extendible-custom'
        options={options}
        handleChange={handleDeliveryFieldChange}
        onAddOption={() => {
          setShowAddOptionModal(true);
        }}
        editable={true}
      />

      <CommonModal
        show={showAddOptionModal}
        title={t('property_field_extendable_new')}
        confirmText={t('general_add')}
        cancelText={t('general_cancel')}
        onConfirm={addOption}
        onCancel={() => setShowAddOptionModal(false)}
        theme='dark'
      >
        <input
          type='text'
          ref={stockFieldInputRef}
          className='box-border w-full outline-none text-white p-1.5 rounded-md bg-blue-400/20 border border-blue-400 focus:ring-1 focus:ring-blue-500 transition'
        />
      </CommonModal>
    </>
  );
}
